import { useRef, useEffect, useState } from 'react'
import styled from 'astroturf/react'
import { useDeviceType } from '@calendly/ui/hooks/use-device-type'
import { useDispatch, useSelector } from 'react-redux'
import { parseQuery } from '@calendly/http-tools'
import { useFlag } from '@calendly/feature-check-react'
import {
  SpotList,
  SpotlistSubtitle as Subtitle,
  SpotlistWrapper as Wrapper,
  SpotlistList as SpotlistListBase,
  SelectTimeTitle as Title,
  SpotListDuration as Duration
} from '@calendly/booking-kit'
import { TitleText } from '@calendly/ui/components/typography'
const spacing0_5x = 'var(--spacing-0-5x, 0.5rem)'
const spacing1x = 'var(--spacing-1x, 1rem)'
const colorIconSuccess = 'var(--colorIconSuccess, var(--coreColorGreen700))'
const primaryColor = 'var(--primary-color, rgb(0, 105, 255))'

import { useI18n } from 'locales'
import { syncSpotUrlParameters } from 'actions/navigation'
import { selectSpot, addSpotToCheckQueue, removeSpotFromCheckQueue, addOrRemoveSpot, confirmSpot } from 'actions/spot'
import { daySelector, selectedSpotsSelector } from 'selectors/calendar'
import { spotsWithConflictsSelector } from 'selectors/mutual_availability'
import { currentUserLocaleSelector, currentUserSelector } from 'selectors/user'
import {
  isPollSelector
} from 'selectors/event_type'
import { eventTypeSelector } from 'root-api'
import { currentInviteeSelector } from 'selectors/current_invitee'
import {
  timezoneSelector,
  selectedDayCalendarSelector,
  spotsSelector,
  timeNotationSelector
} from 'selectors'
import MutualAvailabilityNotice from 'components/select_date_time/mutual_availability_notice'
import { isDiagnosticsEnabledSelector } from 'selectors/diagnostics'
import { MutualAvailability } from 'components/mutual_availability'
import { useMutualAvailabilityEnabled } from 'components/mutual_availability/use_mutual_availability_enabled'
import { formatDate } from 'services/spot_time'
import Timezone from 'components/select_date_time/timezone'

import { useDetectConflicts } from '../../mutual_availability/use_detect_conflicts'

import { Navigator } from './navigator'
import _List from "times-List.module.css!=!astroturf/inline-loader?style=1!/app/frontend/components/select_date_time/spotpicker/times.js?styleId=List";
import _TimesHeaderRow from "times-TimesHeaderRow.module.css!=!astroturf/inline-loader?style=1!/app/frontend/components/select_date_time/spotpicker/times.js?styleId=TimesHeaderRow";
import _DateSubtitle from "times-DateSubtitle.module.css!=!astroturf/inline-loader?style=1!/app/frontend/components/select_date_time/spotpicker/times.js?styleId=DateSubtitle";
import _PhoneTimesHeaderRow from "times-PhoneTimesHeaderRow.module.css!=!astroturf/inline-loader?style=1!/app/frontend/components/select_date_time/spotpicker/times.js?styleId=PhoneTimesHeaderRow";
import _PhoneTimesTitle from "times-PhoneTimesTitle.module.css!=!astroturf/inline-loader?style=1!/app/frontend/components/select_date_time/spotpicker/times.js?styleId=PhoneTimesTitle";
import _PhoneTimezoneWrap from "times-PhoneTimezoneWrap.module.css!=!astroturf/inline-loader?style=1!/app/frontend/components/select_date_time/spotpicker/times.js?styleId=PhoneTimezoneWrap";


const List = /*#__PURE__*/styled(SpotlistListBase, null, {
  displayName: "List",
  styles: _List
})

const TimesHeaderRow = /*#__PURE__*/styled('div', null, {
  displayName: "TimesHeaderRow",
  styles: _TimesHeaderRow
})

const DateSubtitle = /*#__PURE__*/styled(Subtitle, null, {
  displayName: "DateSubtitle",
  styles: _DateSubtitle
})

const PhoneTimesHeaderRow = /*#__PURE__*/styled('div', null, {
  displayName: "PhoneTimesHeaderRow",
  styles: _PhoneTimesHeaderRow
})

const PhoneTimesTitle = /*#__PURE__*/styled(TitleText, null, {
  displayName: "PhoneTimesTitle",
  styles: _PhoneTimesTitle
})

const PhoneTimezoneWrap = /*#__PURE__*/styled('div', null, {
  displayName: "PhoneTimezoneWrap",
  styles: _PhoneTimezoneWrap
})

const SpotListComponent = () => {
  const { i18n } = useI18n()
  const dispatch = useDispatch()
  const day = useSelector(daySelector)
  const eventType = useSelector(eventTypeSelector)
  const {
    duration,
    display_group_occupancy: displayGroupOccupancy,
    multi_publisher: isMultiPublisherEventType,
    kind_name: kindName,
    locale: eventTypeLocale
  } = eventType || {}
  const listRef = useRef()
  const isPhone = useDeviceType('isPhone')
  const isTabletUp = useDeviceType('isTabletUp')
  const troubleshootingEnabled = useSelector(isDiagnosticsEnabledSelector)
  const isMutualAvailabilityEnabled = useMutualAvailabilityEnabled()

  const isPoll = useSelector(isPollSelector)
  const calendar = useSelector(selectedDayCalendarSelector)
  const spots = useSelector(spotsSelector)
  const spotsWithConflicts = useSelector(spotsWithConflictsSelector)
  const timeNotation = useSelector(timeNotationSelector)
  const timezone = useSelector(timezoneSelector)
  const selectedSpots = useSelector(selectedSpotsSelector)
  const spotAutofocus = useSelector((state) => state.calendar.spotAutofocus)
  const selectedSpot = useSelector((state) => state.calendar.spots[0])
  const isPreviewEmbed = parseQuery(window.location.search).embed_type === 'preview'
  const publisherLocale = useSelector(currentUserLocaleSelector)
  const invitee = useSelector(currentInviteeSelector)
  const { date_notation: publisherDateNotation } = useSelector(currentUserSelector) || {}
  const [checkSpotForConflicts, uncheckSpotForConflicts] = useDetectConflicts()
  const bookingAppRedesign = useFlag('booking_app_redesign', false)

  const onTimeSelectDefault = (spot) => {
    const { status, available_codes: availableCodes } = spot

    dispatch(removeSpotFromCheckQueue(selectedSpot))
    dispatch(addSpotToCheckQueue(spot))

    if (spot === selectedSpot && (status === 'unavailable' || !!availableCodes)) {
      dispatch(selectSpot(null))
    } else {
      dispatch(selectSpot(spot))
    }
  }

  const onTimeSelectPoll = (spot) => {
    dispatch(addOrRemoveSpot(spot))
    dispatch(syncSpotUrlParameters())
  }

  const onConfirmSpot = (spot) => {
    dispatch(confirmSpot(spot))
  }

  useEffect(() => {
    if (!listRef.current) return
    window.scrollTo(0, 0) // mobile
    listRef.current.scrollTop = 0 // desktop
  }, [day])

  if (!day) return null

  // Prioritize invitee locale (for rescheduling), then event type locale, then publisher locale
  const dateLocale = invitee?.locale || eventTypeLocale || publisherLocale
  const dateFormatKey = bookingAppRedesign
    ? 'time.formats.date_small_with_day'
    : 'time.formats.date_extra'
  const subtitle = formatDate(day.date, i18n.t(dateFormatKey), dateLocale)
  const selectTimeHeading = isPoll
    ? i18n.t('booking.templates.poll.meeting_poll_label')
    : i18n.t('booking.templates.select_a_time')

  return (
    <>
      {bookingAppRedesign && isTabletUp ? (
        <TimesHeaderRow data-component="spotpicker-times-subtitle-row">
          <DateSubtitle data-component="spotpicker-times-subtitle">{subtitle}</DateSubtitle>
          <Timezone variant="minimal" compactInline />
        </TimesHeaderRow>
      ) : (
        isTabletUp && <Subtitle data-component="spotpicker-times-subtitle">{subtitle}</Subtitle>
      )}
      <List ref={listRef} data-component="spotpicker-times-list" troubleshooting={troubleshootingEnabled}>
        {isPhone && !bookingAppRedesign && <Duration value={duration} />}
        {bookingAppRedesign && isPhone && (
          <PhoneTimesHeaderRow data-component="spotpicker-phone-times-header">
            <PhoneTimesTitle as="h2" size="medium" weight="bold">
              {selectTimeHeading}
            </PhoneTimesTitle>
            <PhoneTimezoneWrap>
              <Timezone variant="minimal" compactInline />
            </PhoneTimezoneWrap>
          </PhoneTimesHeaderRow>
        )}
        <MutualAvailabilityNotice />
        <SpotList
          isPoll={isPoll}
          calendar={calendar}
          spots={spots}
          spotsWithConflicts={spotsWithConflicts}
          timeNotation={timeNotation}
          timezone={timezone}
          spotAutofocus={spotAutofocus}
          selectedSpots={selectedSpots}
          selectedSpot={selectedSpot}
          isPreviewEmbed={isPreviewEmbed}
          day={day}
          diagnosticsEnabled={troubleshootingEnabled}
          locale={publisherLocale}
          kindName={kindName}
          isMultiPublisherEventType={isMultiPublisherEventType}
          isMutualAvailabilityEnabled={isMutualAvailabilityEnabled}
          displayGroupOccupancy={displayGroupOccupancy}
          publisherDateNotation={publisherDateNotation}
          listRef={listRef}
          onTimeSelectDefault={onTimeSelectDefault}
          onTimeSelectPoll={onTimeSelectPoll}
          onConfirmSpot={onConfirmSpot}
          checkSpotForConflicts={checkSpotForConflicts}
          uncheckSpotForConflicts={uncheckSpotForConflicts}
        />
      </List>
    </>
  )
}

const MobileViewLayout = ({ children, currentTab, setCurrentTab }) => (
  <>
    <Navigator currentTab={currentTab} setCurrentTab={setCurrentTab} />
    {children}
  </>
)

const MobileView = () => {
  const [currentTab, setCurrentTab] = useState('time')

  return (
    <MobileViewLayout currentTab={currentTab} setCurrentTab={setCurrentTab}>
      {currentTab === 'time' ? <SpotListComponent /> : <MutualAvailability />}
    </MobileViewLayout>
  )
}

const Times = () => {
  const isPhone = useDeviceType('isPhone')
  const day = useSelector(daySelector)
  const bookingAppRedesign = useFlag('booking_app_redesign', false)
  const isMutualAvailabilityEnabled = useMutualAvailabilityEnabled()

  if (!day) return null

  return (
    <Wrapper data-component="spotpicker-times">
      {isPhone && !isMutualAvailabilityEnabled && !bookingAppRedesign && (
        <Title />
      )}
      {isPhone && isMutualAvailabilityEnabled ? (
        <MobileView />
      ) : (
        <SpotListComponent />
      )}
    </Wrapper>
  )
}

export default Times
